// Edit a SCHEDULED training session (Session AFL loop E): the facts the certificate is later
// worked out from, before anything has been issued.
//
// WHY IT EXISTS. The server has always accepted these edits while a session is scheduled, and the
// detail page told the office to make them ("Add lifts by editing the session while it is
// scheduled"), but there was no edit surface anywhere: the only way to correct a mistyped date or
// add a forgotten lift was to delete the session and re-enter it. This is that missing screen.
//
// It is deliberately offered ONLY while the session is SCHEDULED, exactly matching the server's
// freeze. Once the certificate is issued the date, the type and the lift set are what the customer
// already holds, so this modal is not rendered at all rather than opening and then being refused.
//
// It edits the facts, not the record's content: the attendees and the two notes fields have their
// own always-available cards on the page, so they are not duplicated here.
import { useCallback, useEffect, useState } from "react";
import { assets as assetsResource } from "../../lib/resources";
import type { Asset } from "../../lib/resources";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { TRAINING_TYPES, TRAINING_TYPE_LABELS } from "../../lib/trainingTypes";
import { AssignEngineerField } from "./AssignEngineerField";
import type { TrainingSessionDetailDto, TrainingSessionUpdateInput, TrainingType } from "../../lib/trainingTypes";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const ghostBtn =
  "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50";

function eleRef(a: Asset): string {
  return a.eleNumber ?? a.legacyEleNumber ?? "Not allocated";
}

export function EditTrainingSessionModal({
  open,
  session,
  onClose,
  onSave,
}: {
  open: boolean;
  session: TrainingSessionDetailDto;
  onClose: () => void;
  /** Saves the change and reports whether it landed; the page owns the error message either way. */
  onSave: (body: TrainingSessionUpdateInput) => Promise<boolean>;
}) {
  const [trainingType, setTrainingType] = useState<TrainingType>(session.trainingType);
  const [customLabel, setCustomLabel] = useState(session.customLabel ?? "");
  // The stored date is a calendar fact at UTC midnight, so the day is read straight off the ISO
  // string. Building a local Date first would shift it a day back west of Greenwich.
  const [sessionDate, setSessionDate] = useState(session.sessionDate.slice(0, 10));
  const [durationMinutes, setDurationMinutes] = useState(session.durationMinutes == null ? "" : String(session.durationMinutes));
  const [deliveredBy, setDeliveredBy] = useState(session.deliveredBy ?? "");
  // Session AGG: whose phone this session appears on. "" = unassigned (any engineer at the site).
  const [assignedEngineerId, setAssignedEngineerId] = useState(session.assignedEngineerId ?? "");
  const [assetIds, setAssetIds] = useState<string[]>(session.lifts.map((l) => l.assetId));
  const [saving, setSaving] = useState(false);

  const [siteLifts, setSiteLifts] = useState<Asset[]>([]);
  const [liftsLoaded, setLiftsLoaded] = useState(false);
  const [liftsError, setLiftsError] = useState(false);

  // Re-seed every time the modal opens, so a cancelled edit never leaks into the next one and the
  // form always starts from what is actually on file.
  useEffect(() => {
    if (!open) return;
    setTrainingType(session.trainingType);
    setCustomLabel(session.customLabel ?? "");
    setSessionDate(session.sessionDate.slice(0, 10));
    setDurationMinutes(session.durationMinutes == null ? "" : String(session.durationMinutes));
    setDeliveredBy(session.deliveredBy ?? "");
    setAssignedEngineerId(session.assignedEngineerId ?? "");
    setAssetIds(session.lifts.map((l) => l.assetId));
    setSaving(false);
  }, [open, session]);

  const loadLifts = useCallback(() => {
    setLiftsLoaded(false);
    setLiftsError(false);
    assetsResource
      .list({ siteId: session.siteId })
      .then((rows) => {
        setSiteLifts(rows.filter((a) => (a.assetClass ?? "lift") === "lift"));
        setLiftsLoaded(true);
      })
      .catch(() => {
        setLiftsLoaded(true);
        setLiftsError(true);
      });
  }, [session.siteId]);

  useEffect(() => {
    if (!open) return;
    loadLifts();
  }, [open, loadLifts]);

  if (!open) return null;

  function toggleLift(id: string) {
    setAssetIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function submit() {
    setSaving(true);
    const ok = await onSave({
      trainingType,
      customLabel: trainingType === "OTHER" && customLabel.trim() ? customLabel.trim() : null,
      sessionDate,
      durationMinutes: durationMinutes.trim() ? Number(durationMinutes) : null,
      deliveredBy: deliveredBy.trim() || null,
      assignedEngineerId: assignedEngineerId || null,
      assetIds,
    });
    if (ok) onClose();
    else setSaving(false);
  }

  const canSave = sessionDate.trim().length > 0 && !saving;

  return (
    <ModalShell
      ariaLabel="Edit training session"
      onClose={() => !saving && onClose()}
      width="max-w-lg"
      panelClassName="flex max-h-[88vh] flex-col overflow-y-auto p-5"
    >
      <h2 className="font-heading text-xl text-ele-strong">Edit training session</h2>
      <p className="mb-4 text-xs text-ele-body">
        These are the facts the certificate is worked out from. They can be changed until the session is marked as completed.
      </p>

      <div className="space-y-3">
        <div>
          <label className={labelCls} htmlFor="edit-training-type">
            Training type
          </label>
          <select
            id="edit-training-type"
            value={trainingType}
            onChange={(e) => setTrainingType(e.target.value as TrainingType)}
            className={inputCls}
          >
            {TRAINING_TYPES.map((t) => (
              <option key={t} value={t}>
                {TRAINING_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </div>

        {trainingType === "OTHER" && (
          <div>
            <label className={labelCls} htmlFor="edit-training-custom-label">
              What was the training?
            </label>
            <input
              id="edit-training-custom-label"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              placeholder="For example: Machine room access briefing"
              className={inputCls}
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls} htmlFor="edit-training-date">
              Date
            </label>
            <input
              id="edit-training-date"
              type="date"
              value={sessionDate}
              onChange={(e) => setSessionDate(e.target.value)}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls} htmlFor="edit-training-duration">
              Duration in minutes (optional)
            </label>
            <input
              id="edit-training-duration"
              type="number"
              min={1}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              onFocus={(e) => e.currentTarget.select()}
              className={inputCls}
            />
          </div>
        </div>

        <div>
          <label className={labelCls} htmlFor="edit-training-delivered-by">
            Delivered by (internal, never shown to the customer)
          </label>
          <input
            id="edit-training-delivered-by"
            value={deliveredBy}
            onChange={(e) => setDeliveredBy(e.target.value)}
            className={inputCls}
          />
        </div>

        <AssignEngineerField
          id="edit-training-assigned-engineer"
          value={assignedEngineerId}
          onChange={setAssignedEngineerId}
          labelClassName={labelCls}
          selectClassName={inputCls}
        />

        <fieldset>
          <legend className={labelCls}>Lifts covered (optional)</legend>
          {liftsError ? (
            <p className="text-sm text-ele-danger">
              Could not load this site&apos;s lifts.{" "}
              <button type="button" onClick={loadLifts} className="text-ele-green underline">
                Retry
              </button>
            </p>
          ) : !liftsLoaded ? (
            <p className="text-sm text-ele-body">Loading lifts...</p>
          ) : siteLifts.length === 0 ? (
            <p className="text-sm text-ele-body">No lifts are on file for this site.</p>
          ) : (
            <div className="space-y-1">
              {siteLifts.map((l) => (
                <label key={l.id} className="flex items-center gap-2 text-sm text-ele-strong">
                  <input type="checkbox" checked={assetIds.includes(l.id)} onChange={() => toggleLift(l.id)} />
                  <span className="ele-ref">{eleRef(l)}</span>
                  <span className="text-ele-body">{l.liftType ?? ""}</span>
                </label>
              ))}
              <p className="text-xs text-ele-body">Leave every lift unticked for training about the building rather than one lift.</p>
            </div>
          )}
        </fieldset>
      </div>

      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={saving} className={`${ghostBtn} ${MODAL_ACTION_BTN}`}>
          Cancel
        </button>
        <Button onClick={() => void submit()} disabled={!canSave} className={MODAL_ACTION_BTN}>
          {saving ? "Saving..." : "Save changes"}
        </Button>
      </div>
      {sessionDate.trim().length === 0 && <p className="mt-2 text-right text-xs text-ele-body">Choose a date to save.</p>}
    </ModalShell>
  );
}
